import React, { useState, useEffect, useRef, useContext } from 'react';
import axios from 'axios';
import API_BASE_URL from '../config';
import './DetailComponents.css';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import AddHotelModal from './AddProposalHotelModal';
import AddProposalRec from './AddProposalRecModal';
import CreateProposalModal from './CreateProposalModal';
import ProposalViewer from './ProposalViewer';
import ConvertToItineraryModal from './ConvertToItineraryModal';

function ProposalDetails({ proposalId, isDashboard = false, onProposalUpdate }) {
  const [proposal, setProposal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showMenu, setShowMenu] = useState(false);
  const [showAddHotel, setShowAddHotel] = useState(false);
  const [showAddRec, setShowAddRec] = useState(false);
  const [editingHotel, setEditingHotel] = useState(null);
  const [showEditProposal, setShowEditProposal] = useState(false);
  const [previewAsClient, setPreviewAsClient] = useState(false);
  const [showConvertModal, setShowConvertModal] = useState(false);
  const [activeMainTab, setActiveMainTab] = useState('hotels');
  const [activeDestinationTab, setActiveDestinationTab] = useState('');
  const [activeRecosTab, setActiveRecosTab] = useState('things-to-do');
  
  const { id: urlId } = useParams();
  const id = proposalId || urlId;
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const menuRef = useRef(null);
  const modalRef = useRef(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close modal when clicking outside
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setShowAddHotel(false);
      }
    };
    if (showAddHotel) {
      document.addEventListener('mousedown', handleModalClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleModalClickOutside);
  }, [showAddHotel]);

  const fetchProposal = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/proposals/${id}`);
      setProposal(response.data.data);
    } catch (err) {
      setError('Failed to load proposal details');
      console.error('Error fetching proposal:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchProposal();
    }
  }, [id]);

  const handleRefresh = async () => {
    setLoading(true);
    await fetchProposal();
  };

  // Set active destination tab when proposal loads, but preserve current selection if valid
  useEffect(() => {
    if (proposal?.destinations?.length > 0) {
      // Only set to first destination if no current selection or current selection is invalid
      if (!activeDestinationTab || !proposal.destinations.includes(activeDestinationTab)) {
        setActiveDestinationTab(proposal.destinations[0]);
      }
    }
  }, [proposal, activeDestinationTab]);

  const handleBack = () => {
    navigate('/proposals');
  };

  const handleAddHotel = () => {
    setShowMenu(false);
    setShowAddHotel(true);
    setEditingHotel(null); // Clear editing state for new hotel
  };

  const handleAddRec = () => {
    setShowMenu(false);
    setShowAddRec(true);
  };

  const handleEditHotel = (hotel) => {
    setEditingHotel(hotel);
    setShowAddHotel(true);
  };

  const handleDeleteProposal = () => {
    if (window.confirm('Are you sure you want to delete this proposal?')) {
      axios.delete(`${API_BASE_URL}/api/proposals/${id}`)
        .then(() => {
          if (onProposalUpdate) {
            onProposalUpdate();
          }
          navigate('/admin-dashboard?view=proposals');
        })
        .catch(() => setError('Failed to delete proposal'));
    }
    setShowMenu(false);
  };

  const handleEditProposal = () => {
    setShowEditProposal(true);
    setShowMenu(false);
  };

  const handlePreviewAsClient = () => {
    setPreviewAsClient(true);
    setShowMenu(false);
  };

  const handleConvertToItinerary = () => {
    setShowConvertModal(true);
    setShowMenu(false);
  };

  const handleConvertModalClose = () => {
    setShowConvertModal(false);
  };

  const handleConvertSubmit = async (selectedItems) => {
    try {
      // Prepare trip data from proposal
      const tripData = {
        title: proposal.title,
        destinations: proposal.destinations || [],
        startDate: proposal.startDate,
        endDate: proposal.endDate,
        clientId: proposal.clientId._id || proposal.clientId, // Handle if clientId is an object
        bookings: [], // Will be populated in next phase
        travelAssociateIds: [],
        showItinerary: false
      };

      // Create the trip
      const response = await axios.post(`${API_BASE_URL}/api/trips`, tripData);
      
      // Check if trip was created successfully
      if (response.data && response.data.data) {
        // Close modal
        setShowConvertModal(false);
        
        // Trigger dashboard refresh if in dashboard mode
        if (onProposalUpdate) {
          onProposalUpdate(null, true);
        }
        
        // Show success message
        alert('Itinerary created successfully! Check the Itineraries tab.');
      } else {
        // Handle unexpected response
        setShowConvertModal(false);
        alert('Trip may have been created but unexpected response format.');
      }
    } catch (error) {
      alert('Failed to create itinerary: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleExitPreview = () => {
    setPreviewAsClient(false);
  };

  const toggleMenu = () => setShowMenu(!showMenu);

  const handleTogglePublish = () => {
    const newStatus = proposal.status === 'published' ? 'draft' : 'published';
    const updatedProposal = { ...proposal, status: newStatus };
    
    axios.put(`${API_BASE_URL}/api/proposals/${id}`, updatedProposal)
      .then(response => {
        setProposal(response.data.data);
      })
      .catch(error => {
        console.error('Error updating proposal status:', error);
      });
  };

  const handleDeleteHotel = async (hotelId) => {
    if (window.confirm('Are you sure you want to delete this hotel from the proposal?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/proposals/${id}/hotels/${hotelId}`);
        
        // Update local state to remove the deleted hotel
        setProposal(prevProposal => ({
          ...prevProposal,
          hotels: prevProposal.hotels.filter(hotel => hotel._id !== hotelId)
        }));
      } catch (error) {
        console.error('Error deleting hotel:', error);
        setError('Failed to delete hotel');
      }
    }
  };

  const handleDeleteRecommendation = async (recIndex) => {
    if (window.confirm('Are you sure you want to delete this recommendation from the proposal?')) {
      try {
        await axios.delete(`${API_BASE_URL}/api/proposals/${id}/recommendations/${recIndex}`);
        
        // Update local state to remove the deleted recommendation
        setProposal(prevProposal => ({
          ...prevProposal,
          recommendations: prevProposal.recommendations.filter((_, index) => index !== recIndex)
        }));
      } catch (error) {
        console.error('Error deleting recommendation:', error);
        setError('Failed to delete recommendation');
      }
    }
  };

  const formatDate = (date) => {
    if (!date) return 'Not set';

    // Handle date strings in YYYY-MM-DD format to avoid timezone conversion issues
    let dateObj;
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Parse YYYY-MM-DD string manually to avoid timezone issues
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
    } else {
      dateObj = new Date(date);
    }

    const year = dateObj.getFullYear();
    const month = dateObj.getMonth();
    const day = dateObj.getDate();

    // Create full date string in format "January 1, 2025"
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                       'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[month]} ${day}, ${year}`;
  };

  // Short date format for hotel cards only
  const formatShortDate = (date) => {
    if (!date) return 'Not set';

    let dateObj;
    if (typeof date === 'string' && date.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = date.split('-').map(Number);
      dateObj = new Date(year, month - 1, day);
    } else {
      dateObj = new Date(date);
    }

    const month = dateObj.getMonth();
    const day = dateObj.getDate();

    // Short format: "Jan 1"
    const shortMonthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${shortMonthNames[month]} ${day}`;
  };

  // Calculate number of nights between two dates
  const calculateNights = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);

    // Calculate difference in milliseconds
    const diffTime = Math.abs(end - start);

    // Convert to days and return
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return diffDays;
  };

  // Generate calendar dates - EXACT proposal date range only
  const generateCalendarDates = () => {
    const dates = [];
    
    if (!proposal || !proposal.startDate || !proposal.endDate) {
      return [];
    }
    
    try {
      // Parse dates and ensure they're valid
      const start = new Date(proposal.startDate);
      const end = new Date(proposal.endDate);
      
      // Validate date range
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        return [];
      }
      
      if (start > end) {
        return [];
      }
      
      // Generate dates from start to end (inclusive)
      const currentDate = new Date(start);
      currentDate.setHours(0, 0, 0, 0); // Start of day
      
      const lastDate = new Date(end);
      lastDate.setHours(23, 59, 59, 999); // End of day
      
      // Loop through each day
      while (currentDate <= lastDate) {
        dates.push(new Date(currentDate));
        currentDate.setDate(currentDate.getDate() + 1);
      }
      
      return dates;
      
    } catch (error) {
      return [];
    }
  };

  // Group dates by month for better display
  const groupDatesByMonth = (dates) => {
    const grouped = {};
    dates.forEach(date => {
      const monthKey = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      if (!grouped[monthKey]) {
        grouped[monthKey] = [];
      }
      grouped[monthKey].push(date);
    });
    return grouped;
  };

  // Calculate detailed cost breakdown for a hotel
  const calculateHotelBreakdown = (hotel) => {
    if (!hotel.dateRates || typeof hotel.dateRates !== 'object') {
      return { subtotal: 0, taxesAndFees: 0, discounts: 0, total: 0, breakdown: [] };
    }


    // Calculate base subtotal from daily rates
    let grossSubtotal = 0;
    Object.values(hotel.dateRates).forEach(rate => {
      const rateValue = Math.round((parseFloat(rate) || 0) * 100) / 100;
      grossSubtotal += rateValue;
    });

    let breakdown = [];
    let totalTaxesAndFees = 0;
    let totalDiscounts = 0;

    // Check if tax is included in the rates
    const taxIncluded = hotel.taxIncluded === true;

    // Calculate taxes and fees separately
    let taxBreakdown = [];
    let feeBreakdown = [];
    let netSubtotal = grossSubtotal;

    // Process taxes (affected by taxIncluded checkbox)
    if (hotel.taxes && Array.isArray(hotel.taxes)) {
      if (taxIncluded) {
        // When tax is included, calculate inverse: net = gross / (1 + tax_rate)
        let totalTaxRate = 0;

        // First pass: calculate total tax rate
        hotel.taxes.forEach(item => {
          if (item.type === 'percentage') {
            const rate = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            totalTaxRate += rate / 100; // Convert to decimal
          }
        });

        // Calculate net subtotal using inverse formula
        if (totalTaxRate > 0) {
          netSubtotal = grossSubtotal / (1 + totalTaxRate);
        }

        // Second pass: calculate individual tax amounts based on net subtotal
        hotel.taxes.forEach(item => {
          const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
          let calculatedAmount = 0;

          if (item.type === 'percentage') {
            calculatedAmount = netSubtotal * (amount / 100);
          } else {
            calculatedAmount = amount;
          }

          taxBreakdown.push({
            title: item.title,
            amount: calculatedAmount,
            display: item.type === 'percentage' ? `${amount}%` : `$${amount.toFixed(2)}`,
            type: 'tax',
            percentage: item.type === 'percentage' ? amount : null
          });
        });
      } else {
        // When tax is not included, calculate normally
        hotel.taxes.forEach(item => {
          const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
          let calculatedAmount = 0;

          if (item.type === 'percentage') {
            calculatedAmount = grossSubtotal * (amount / 100);
          } else {
            calculatedAmount = amount;
          }

          taxBreakdown.push({
            title: item.title,
            amount: calculatedAmount,
            display: item.type === 'percentage' ? `${amount}%` : `$${amount.toFixed(2)}`,
            type: 'tax',
            percentage: item.type === 'percentage' ? amount : null
          });
        });
      }
    }

    // Process fees (always added to total, not affected by taxIncluded)
    if (hotel.fees && Array.isArray(hotel.fees)) {
      hotel.fees.forEach(item => {
        const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
        let calculatedAmount = amount; // Fees are always fixed amounts

        feeBreakdown.push({
          title: item.title,
          amount: calculatedAmount,
          display: `$${amount.toFixed(2)}`,
          type: 'fee'
        });
      });
    }

    // Legacy support for taxesAndFees (for backward compatibility)
    if (hotel.taxesAndFees && Array.isArray(hotel.taxesAndFees)) {
      // Separate taxes and fees from legacy taxesAndFees array
      const legacyTaxes = hotel.taxesAndFees.filter(item => item.type === 'percentage');
      const legacyFees = hotel.taxesAndFees.filter(item => item.type === 'fixed');

      // Process legacy taxes (affected by taxIncluded checkbox)
      if (legacyTaxes.length > 0) {
        if (taxIncluded) {
          // When tax is included, calculate inverse: net = gross / (1 + tax_rate)
          let totalTaxRate = 0;

          // First pass: calculate total tax rate
          legacyTaxes.forEach(item => {
            const rate = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            totalTaxRate += rate / 100; // Convert to decimal
          });

          // Calculate net subtotal using inverse formula
          if (totalTaxRate > 0) {
            netSubtotal = grossSubtotal / (1 + totalTaxRate);
          }

          // Second pass: calculate individual tax amounts based on net subtotal
          legacyTaxes.forEach(item => {
            const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            const calculatedAmount = netSubtotal * (amount / 100);

            taxBreakdown.push({
              title: item.title,
              amount: calculatedAmount,
              display: `${amount}%`,
              type: 'tax',
              percentage: amount
            });
          });
        } else {
          // When tax is not included, calculate normally
          legacyTaxes.forEach(item => {
            const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
            const calculatedAmount = grossSubtotal * (amount / 100);

            taxBreakdown.push({
              title: item.title,
              amount: calculatedAmount,
              display: `${amount}%`,
              type: 'tax',
              percentage: amount
            });
          });
        }
      }

      // Process legacy fees (always added to total, not affected by taxIncluded)
      legacyFees.forEach(item => {
        const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
        
        feeBreakdown.push({
          title: item.title,
          amount: amount,
          display: `$${amount.toFixed(2)}`,
          type: 'fee'
        });
      });
    }

    // Legacy tax percentage support
    if (hotel.taxPercentage && hotel.taxPercentage > 0) {
      if (taxIncluded) {
        // Use inverse calculation for legacy tax
        const taxRate = hotel.taxPercentage / 100;
        netSubtotal = grossSubtotal / (1 + taxRate);
        const legacyTaxAmount = netSubtotal * taxRate;

        taxBreakdown.push({
          title: 'Tax',
          amount: legacyTaxAmount,
          display: `${hotel.taxPercentage}%`,
          type: 'percentage',
          percentage: hotel.taxPercentage
        });
      } else {
        // Normal calculation for legacy tax
        const legacyTaxAmount = grossSubtotal * (hotel.taxPercentage / 100);
        taxBreakdown.push({
          title: 'Tax',
          amount: legacyTaxAmount,
          display: `${hotel.taxPercentage}%`,
          type: 'percentage',
          percentage: hotel.taxPercentage
        });
      }
    }

    // Apply tax calculations based on whether taxes are included or not
    if (!taxIncluded) {
      // Tax not included: add taxes to total
      taxBreakdown.forEach(tax => {
        totalTaxesAndFees += tax.amount;
        breakdown.push({
          type: 'tax',
          title: tax.title,
          amount: tax.amount,
          display: tax.display
        });
      });
    } else {
      // Tax included: show taxes but don't add to total (already included in gross subtotal)
      taxBreakdown.forEach(tax => {
        breakdown.push({
          type: 'tax',
          title: tax.title,
          amount: tax.amount,
          display: tax.display,
          included: true
        });
      });
    }

    // Apply fee calculations (fees are always added to total, not affected by taxIncluded)
    feeBreakdown.forEach(fee => {
      totalTaxesAndFees += fee.amount;
      breakdown.push({
        type: 'fee',
        title: fee.title,
        amount: fee.amount,
        display: fee.display
      });
    });

    // Calculate discounts based on net subtotal
    if (hotel.discounts && Array.isArray(hotel.discounts)) {
      hotel.discounts.forEach(item => {
        const amount = Math.round((parseFloat(item.amount) || 0) * 100) / 100;
        let calculatedAmount = 0;

        if (item.type === 'percentage') {
          calculatedAmount = netSubtotal * (amount / 100);
        } else {
          calculatedAmount = amount;
        }

        totalDiscounts += calculatedAmount;
        breakdown.push({
          type: 'discount',
          title: item.title,
          amount: calculatedAmount,
          display: item.type === 'percentage' ? `${amount}%` : `$${amount.toFixed(2)}`
        });
      });
    }

    // Calculate total based on whether taxes are included or not
    let total = 0;
    if (taxIncluded) {
      // If taxes are included in rates, use gross subtotal (which includes taxes)
      total = grossSubtotal + totalTaxesAndFees - totalDiscounts;
    } else {
      // If taxes are not included, use net subtotal and add taxes
      total = netSubtotal + totalTaxesAndFees - totalDiscounts;
    }


    return {
      subtotal: netSubtotal, // Always show net subtotal (without tax if tax is included)
      taxesAndFees: totalTaxesAndFees,
      discounts: totalDiscounts,
      total,
      breakdown,
      taxIncluded,
      grossSubtotal // Keep gross subtotal for display purposes
    };
  };

  // Simple total calculation for backward compatibility
  const calculateHotelTotal = (hotel) => {
    return calculateHotelBreakdown(hotel).total;
  };

  if (loading) return <div className="loading">Loading proposal details...</div>;
  if (error && !showAddHotel) return <div style={{ color: 'red', padding: '20px' }}>{error}</div>;
  if (!proposal) return <div style={{ padding: '20px' }}>Proposal not found</div>;

  const calendarDates = generateCalendarDates();
  const groupedDates = groupDatesByMonth(calendarDates);

  return (
    <div className={isDashboard ? 'dashboard-integrated' : ''}>
      <div className={`App trip-details ${isDashboard ? 'dashboard-mode' : ''}`}>
        {!isDashboard && <h1>Proposal Details</h1>}
        {!isDashboard && (
          <div className="header-section">
            <button className="back-button" onClick={handleBack}>&lt;</button>
          </div>
        )}
        <div className={`info-box trip-details-box ${isDashboard ? 'dashboard-style' : ''}`} style={{ maxWidth: isDashboard ? '100%' : '700px !important', margin: '0 auto' }}>
          {user?.role === 'admin' && (
            <div className={`details-actions ${isDashboard ? 'dashboard-actions' : ''}`} ref={menuRef}>
              {isDashboard ? (
                <div className="dashboard-action-buttons">
                  <button className="dashboard-action-btn" onClick={handleEditProposal}>
                    Edit Proposal
                  </button>
                  <button className="dashboard-action-btn" onClick={handleAddHotel}>
                    Add Hotel
                  </button>
                  <button className="dashboard-action-btn" onClick={handleAddRec}>
                    Add Rec
                  </button>
                  <button className="dashboard-action-btn" onClick={handleConvertToItinerary}>
                    Convert to Itinerary
                  </button>
                  <button className="dashboard-action-btn" onClick={handlePreviewAsClient}>
                    Preview as Client
                  </button>
                  <button className="dashboard-action-btn danger" onClick={handleDeleteProposal}>
                    Delete Proposal
                  </button>
                </div>
              ) : (
                <>
                  <button className="actions-menu-button" onClick={toggleMenu}>⋮</button>
                  {showMenu && (
                    <div className="actions-menu">
                      <div className="actions-menu-item" onClick={handleEditProposal}>Edit Proposal</div>
                      <div className="actions-menu-item" onClick={handleAddHotel}>Add Hotel</div>
                      <div className="actions-menu-item" onClick={handleAddRec}>Add Rec</div>
                      <div className="actions-menu-item" onClick={handleConvertToItinerary}>Convert to Itinerary</div>
                      <div className="actions-menu-item" onClick={handlePreviewAsClient}>Preview as Client</div>
                      <div className="actions-menu-item" onClick={handleDeleteProposal}>Delete Proposal</div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
            <h2 style={{ margin: 0 }}>{proposal.title}</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span className="label">Publish:</span>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={proposal.status === 'published'}
                  onChange={handleTogglePublish}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          <div className="details-box">
            <div className="details-grid">
              {/* Single Column */}
              <div className="details-column">
              {/* Client and Status on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Client:</span> {proposal.clientId ?
                    `${proposal.clientId.name} ${proposal.clientId.lastName1}` :
                    'Unknown Client'}
                </div>
              </div>

              {/* Start Date and End Date on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Start Date:</span> {formatDate(proposal.startDate)}
                </div>
                <div className="details-item">
                  <span className="label">End Date:</span> {formatDate(proposal.endDate)}
                </div>
              </div>

              {/* Nights and # Rooms on same line */}
              <div className="details-row">
                {proposal.startDate && proposal.endDate && (
                  <div className="details-item">
                    <span className="label">Nights:</span> {calculateNights(proposal.startDate, proposal.endDate)}
                  </div>
                )}
                <div className="details-item">
                  <span className="label"># Rooms:</span> {proposal.numberOfRooms || 'N/A'}
                </div>
              </div>

              {/* Travelers and Destinations on same line */}
              <div className="details-row">
                <div className="details-item">
                  <span className="label">Travelers:</span> {proposal.numberOfTravelers || 'N/A'}
                </div>
                {proposal.destinations && proposal.destinations.length > 0 && (
                  <div className="details-item">
                    <span className="label">Destinations:</span> {proposal.destinations.join(', ')}
                  </div>
                )}
              </div>
            </div>
            </div>

                {proposal.description && (
                  <div className="details-section">
                    <div className="details-section-title" style={{ display: 'unset' }}>
                      <h3>Notes</h3>
                    </div>
                    <p style={{ padding: '10px', backgroundColor: '#f8f9fa', borderRadius: '3px' }}>
                      {proposal.description}
                    </p>
                  </div>
                )}


                {/* Hotel Options Section */}
                {proposal.hotels && proposal.hotels.length > 0 && (
                  <div className="details-section">
                    {/* Main Tabs for Hotels, Flights, and Recos */}
                    <div className="main-tabs">
                      <button
                        className={`tab-button ${activeMainTab === 'hotels' ? 'active' : ''}`}
                        onClick={() => setActiveMainTab('hotels')}
                      >
                        Hotels ({proposal.hotels.length})
                      </button>
                      <button
                        className={`tab-button ${activeMainTab === 'flights' ? 'active' : ''}`}
                        onClick={() => setActiveMainTab('flights')}
                      >
                        Flights (0)
                      </button>
                      <button
                        className={`tab-button ${activeMainTab === 'recos' ? 'active' : ''}`}
                        onClick={() => setActiveMainTab('recos')}
                      >
                        Recos
                      </button>
                    </div>

                    {/* Destination Sub-tabs (only show if multiple destinations) */}
                    {proposal.destinations && proposal.destinations.length > 1 && activeMainTab === 'hotels' && (
                      <div className="sub-tabs">
                        {proposal.destinations.map((destination, index) => (
                          <button
                            key={index}
                            className={`sub-tab-button ${activeDestinationTab === destination ? 'active' : ''}`}
                            onClick={() => setActiveDestinationTab(destination)}
                          >
                            {destination}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Recos Sub-tabs */}
                    {activeMainTab === 'recos' && (
                      <div className="sub-tabs">
                        <button
                          className={`sub-tab-button ${activeRecosTab === 'things-to-do' ? 'active' : ''}`}
                          onClick={() => setActiveRecosTab('things-to-do')}
                        >
                          Things to Do
                        </button>
                        <button
                          className={`sub-tab-button ${activeRecosTab === 'restaurants' ? 'active' : ''}`}
                          onClick={() => setActiveRecosTab('restaurants')}
                        >
                          Restaurants
                        </button>
                        <button
                          className={`sub-tab-button ${activeRecosTab === 'activities' ? 'active' : ''}`}
                          onClick={() => setActiveRecosTab('activities')}
                        >
                          Activities
                        </button>
                        <button
                          className={`sub-tab-button ${activeRecosTab === 'events' ? 'active' : ''}`}
                          onClick={() => setActiveRecosTab('events')}
                        >
                          Events
                        </button>
                      </div>
                    )}

                    {/* Content based on active tabs */}
                    {activeMainTab === 'hotels' && (
                      <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                      {proposal.hotels
                        .filter(hotel =>
                          // Filter by destination if multiple destinations exist
                          !proposal.destinations || proposal.destinations.length <= 1 ||
                          hotel.destination === activeDestinationTab
                        )
                        .map((hotel, index) => {
                          const costBreakdown = calculateHotelBreakdown(hotel);
                          const totalCost = costBreakdown.total;

                          return (
                            <div
                              key={hotel._id || index}
                              onClick={() => user?.role === 'admin' && handleEditHotel(hotel)}
                              style={{
                                border: isDashboard ? '1px solid #e9ecef' : '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '15px',
                                backgroundColor: isDashboard ? '#f8f9fa' : '#fff',
                                boxShadow: isDashboard ? 'none' : '0 2px 4px rgba(0,0,0,0.1)',
                                cursor: user?.role === 'admin' ? 'pointer' : 'default',
                                position: 'relative'
                              }}
                            >
                              {/* Delete button - only for admin */}
                              {user?.role === 'admin' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteHotel(hotel._id);
                                  }}
                                  style={{
                                    position: 'absolute',
                                    top: '10px',
                                    right: '10px',
                                    backgroundColor: 'transparent',
                                    color: '#666',
                                    border: '1px solid #ccc',
                                    borderRadius: '50%',
                                    width: '24px',
                                    height: '24px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    zIndex: 1,
                                    transition: 'all 0.2s ease'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#f8f9fa';
                                    e.target.style.borderColor = '#999';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = 'transparent';
                                    e.target.style.borderColor = '#ccc';
                                  }}
                                  title="Delete hotel"
                                >
                                  ×
                                </button>
                              )}

                              {/* Header with Hotel Name, Status, and Delete Button */}
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                {/* Hotel Name on the left */}
                                <div style={{ flex: 1 }}>
                                  <h4 style={{ margin: '0', fontSize: '1.1em' }}>{hotel.name}</h4>
                                  {/* Rate Title */}
                                  {hotel.rateTitle && hotel.rateTitle.trim() && (
                                    <p style={{ margin: '2px 0 0 0', fontSize: '0.85em', color: '#333', fontWeight: '500' }}>
                                      {hotel.rateTitle}
                                    </p>
                                  )}
                                  {hotel.location && !hotel.location.includes('Coordinates:') && (
                                    <p style={{ margin: '2px 0 0 0', fontSize: '0.9em', color: '#666' }}>{hotel.location}</p>
                                  )}
                                </div>

                                {/* Status Badge in the middle/right */}
                                <div style={{ marginRight: '40px' }}>
                                  <span style={{
                                    padding: '4px 8px',
                                    borderRadius: '12px',
                                    fontSize: '0.7em',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    whiteSpace: 'nowrap',
                                    color: 'white',
                                    backgroundColor: hotel.status === 'accepted' ? '#28a745' :
                                                   hotel.status === 'rejected' ? '#dc3545' :
                                                   hotel.status === 'pending' ? '#ffc107' : '#6c757d',
                                    border: hotel.status === 'pending' ? '2px solid #ff8c00' : 'none'
                                  }}>
                                    {hotel.status}
                                  </span>
                                </div>
                              </div>

                              {/* Dates and Cost - Prominent Display */}
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginTop: '10px',
                                padding: '10px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '6px',
                                border: '1px solid #e9ecef'
                              }}>
                                {/* Daily Rates */}
                                <div style={{ flex: 1 }}>
                                  {hotel.dateRates && Object.keys(hotel.dateRates).length > 0 ? (
                                    <div>
                                      <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9em', marginBottom: '5px' }}>
                                        Daily Rates
                                        {costBreakdown.taxIncluded && (
                                          <span style={{ fontSize: '0.7em', marginLeft: '8px', color: '#666' }}>
                                            (tax included)
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ fontSize: '0.8em', lineHeight: '1.3', maxWidth: '140px' }}>
                                        {Object.entries(hotel.dateRates)
                                          .sort(([dateA], [dateB]) => dateA.localeCompare(dateB))
                                          .map(([date, rate], index) => {
                                            const rateValue = Math.round((parseFloat(rate) || 0) * 100) / 100;
                                            const isFree = rateValue === 0;
                                            return (
                                              <div key={index} style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                                marginBottom: '2px',
                                                color: '#333'
                                              }}>
                                                <span>{formatShortDate(date)}</span>
                                                <span style={{ fontWeight: '500' }}>
                                                  {isFree ? 'Free' : `$${rateValue.toFixed(2)}`}
                                                </span>
                                              </div>
                                            );
                                          })}
                                        {Object.keys(hotel.dateRates).length > 1 && (
                                          <div style={{ 
                                            marginTop: '5px', 
                                            paddingTop: '5px', 
                                            borderTop: '1px solid #e9ecef',
                                            fontWeight: 'bold',
                                            fontSize: '0.85em'
                                          }}>
                                            {Object.keys(hotel.dateRates).length} nights total
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: '#999', fontSize: '0.9em', fontStyle: 'italic' }}>
                                      No dates selected
                                    </div>
                                  )}
                                </div>

                                {/* Visual separator line */}
                                <div style={{
                                  height: '1px',
                                  backgroundColor: '#e9ecef',
                                  margin: '10px 0'
                                }}></div>

                                {/* Cost Breakdown */}
                                <div style={{ textAlign: 'right', flex: 1 }}>
                                  {totalCost > 0 ? (
                                    <div style={{ maxWidth: '180px', marginLeft: 'auto' }}>
                                      <div style={{ fontWeight: 'bold', color: '#333', fontSize: '0.9em', marginBottom: '5px' }}>
                                        Cost Breakdown
                                      </div>
                                      
                                      {/* Subtotal */}
                                      <div style={{ fontSize: '0.8em', display: 'flex', justifyContent: 'space-between', marginBottom: '2px' }}>
                                        <span>Subtotal:</span>
                                        <span>${costBreakdown.subtotal.toFixed(2)}</span>
                                      </div>
                                      
                                      {/* Taxes */}
                                      {costBreakdown.breakdown.filter(item => item.type === 'tax').map((item, index) => (
                                        <div key={`tax-${index}`} style={{
                                          fontSize: '0.8em',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          marginBottom: '2px',
                                          color: '#333'
                                        }}>
                                          <span>{item.title} ({item.display})</span>
                                          <span>{item.included ? '' : '+'}${item.amount.toFixed(2)}</span>
                                      </div>
                                      ))}
                                      
                                      {/* Fees */}
                                      {costBreakdown.breakdown.filter(item => item.type === 'fee').map((item, index) => (
                                        <div key={`fee-${index}`} style={{
                                          fontSize: '0.8em',
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          marginBottom: '2px',
                                          color: '#333'
                                        }}>
                                          <span>{item.title}</span>
                                          <span>+${item.amount.toFixed(2)}</span>
                                      </div>
                                      ))}
                                      
                                      {/* Discounts */}
                                      {costBreakdown.breakdown.filter(item => item.type === 'discount').map((item, index) => (
                                        <div key={`discount-${index}`} style={{ fontSize: '0.8em', display: 'flex', justifyContent: 'space-between', marginBottom: '2px', color: '#333' }}>
                                          <span>{item.title} ({item.display}):</span>
                                          <span>-${item.amount.toFixed(2)}</span>
                                      </div>
                                      ))}
                                      
                                      {/* Total */}
                                      <div style={{ 
                                        fontSize: '1.1em', 
                                        fontWeight: 'bold', 
                                        display: 'flex', 
                                        justifyContent: 'space-between',
                                        marginTop: '5px',
                                        paddingTop: '5px',
                                        borderTop: '1px solid #e9ecef'
                                      }}>
                                        <span>Total:</span>
                                        <span>${totalCost.toFixed(2)}</span>
                                        </div>
                                    </div>
                                  ) : (
                                    <div style={{ color: '#999', fontSize: '0.9em', fontStyle: 'italic' }}>
                                      Cost not calculated
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      }
                      </div>
                    )}

                    {/* Flights tab content */}
                    {activeMainTab === 'flights' && (
                      <div style={{ marginTop: '15px' }}>
                        <div style={{
                          padding: '40px',
                          textAlign: 'center',
                          border: '2px dashed #ddd',
                          borderRadius: '8px',
                          color: '#666'
                        }}>
                          <h4>Flight Options</h4>
                          <p>Flight options will be available in a future update.</p>
                        </div>
                      </div>
                    )}

                    {/* Recos tab content */}
                    {activeMainTab === 'recos' && (
                      <div style={{ marginTop: '15px' }}>
                        {/* Things to Do */}
                        {activeRecosTab === 'things-to-do' && (
                          <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                            {proposal.recommendations && proposal.recommendations.filter(rec => rec.recType === 'things-to-do').length > 0 ? (
                              proposal.recommendations
                                .filter(rec => rec.recType === 'things-to-do')
                                .map((rec, index) => {
                                  // Find the actual index in the full recommendations array
                                  const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                                  return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Delete button - only for admin */}
                                      {user?.role === 'admin' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteRecommendation(actualIndex);
                                          }}
                                          className="recommendation-delete-btn"
                                          title="Delete recommendation"
                                        >
                                          ×
                                        </button>
                                      )}
                                      
                                      {/* Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title, Links, and Details */}
                                        <div className="recommendation-left-column">
                                          <div className="recommendation-title-row">
                                            <h4 className="recommendation-title">{rec.thingsToDoTitle}</h4>
                                            {rec.thingsToDoUrl && (
                                              <a
                                                href={rec.thingsToDoUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="recommendation-link-button"
                                              >
                                                Website
                                              </a>
                                            )}
                                            {rec.mapLink && (
                                              <a
                                                href={rec.mapLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="recommendation-link-button"
                                              >
                                                Map
                                              </a>
                                            )}
                                          </div>

                                          {/* Details */}
                                          <div className="recommendation-details">
                                            <div><strong>Time:</strong> {rec.timeDuration?.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                                            {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                          </div>
                                        </div>

                                        {/* Right Column - Client Selected Date */}
                                        <div className="recommendation-right-column">
                                          {rec.selectedDate && (
                                            <div className="recommendation-event-date">
                                              <strong style={{ color: '#0066cc', display: 'block', marginBottom: '4px' }}>Client Selected:</strong>
                                              {new Date(rec.selectedDate).toLocaleString('en-US', { 
                                                month: 'long', 
                                                day: 'numeric', 
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                  </div>
                                );
                                })
                            ) : (
                              <div style={{
                                padding: '40px',
                                textAlign: 'center',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                color: '#666'
                              }}>
                                <h4>Things to Do</h4>
                                <p>Local attractions and must-see places will be available here.</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Restaurants */}
                        {activeRecosTab === 'restaurants' && (
                          <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                            {proposal.recommendations && proposal.recommendations.filter(rec => rec.recType === 'restaurants').length > 0 ? (
                              proposal.recommendations
                                .filter(rec => rec.recType === 'restaurants')
                                .map((rec, index) => {
                                  // Find the actual index in the full recommendations array
                                  const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                                  return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Delete button - only for admin */}
                                      {user?.role === 'admin' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteRecommendation(actualIndex);
                                          }}
                                          className="recommendation-delete-btn"
                                          title="Delete recommendation"
                                        >
                                          ×
                                        </button>
                                      )}
                                      
                                      {/* Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title, Links, and Details */}
                                        <div className="recommendation-left-column">
                                          <div className="recommendation-title-row">
                                            <h4 className="recommendation-title">{rec.restaurantName}</h4>
                                            {rec.googleMapsUrl && (
                                              <a
                                                href={rec.googleMapsUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="recommendation-link-button"
                                              >
                                                Map
                                              </a>
                                            )}
                                            {rec.instagramLink && (
                                              <a
                                                href={rec.instagramLink}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="recommendation-link-button"
                                              >
                                                Instagram
                                              </a>
                                            )}
                                          </div>

                                          {/* Details */}
                                          <div className="recommendation-details">
                                            <div><strong>Type:</strong> {rec.restaurantType?.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                            {rec.cuisineType && <div><strong>Cuisine:</strong> {rec.cuisineType}</div>}
                                            {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                          </div>
                                        </div>

                                        {/* Right Column - Client Selected Date */}
                                        <div className="recommendation-right-column">
                                          {rec.selectedDate && (
                                            <div className="recommendation-event-date">
                                              <strong style={{ color: '#0066cc', display: 'block', marginBottom: '4px' }}>Client Selected:</strong>
                                              {new Date(rec.selectedDate).toLocaleString('en-US', { 
                                                month: 'long', 
                                                day: 'numeric', 
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                  </div>
                                );
                                })
                            ) : (
                              <div style={{
                                padding: '40px',
                                textAlign: 'center',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                color: '#666'
                              }}>
                                <h4>Restaurants</h4>
                                <p>Recommended dining options will be available here.</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Activities */}
                        {activeRecosTab === 'activities' && (
                          <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                            {proposal.recommendations && proposal.recommendations.filter(rec => rec.recType === 'activities').length > 0 ? (
                              proposal.recommendations
                                .filter(rec => rec.recType === 'activities')
                                .map((rec, index) => {
                                  // Find the actual index in the full recommendations array
                                  const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                                  return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Delete button - only for admin */}
                                      {user?.role === 'admin' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteRecommendation(actualIndex);
                                          }}
                                          className="recommendation-delete-btn"
                                          title="Delete recommendation"
                                        >
                                          ×
                                        </button>
                                      )}
                                      
                                      {/* Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title, Links, and Details */}
                                        <div className="recommendation-left-column">
                                          <div className="recommendation-title-row">
                                            <h4 className="recommendation-title">{rec.activityTitle}</h4>
                                            {rec.activityUrl && (
                                              <a
                                                href={rec.activityUrl}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="recommendation-link-button"
                                              >
                                                Website
                                              </a>
                                            )}
                                          </div>

                                          {/* Details */}
                                          <div className="recommendation-details">
                                            <div><strong>Type:</strong> {rec.activityType?.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                            {rec.timesAvailable && rec.timesAvailable.length > 0 && (
                                              <div><strong>Available Times:</strong> {[...rec.timesAvailable].sort((a, b) => {
                                                // Sort times in ascending order (24-hour format)
                                                const [aHours, aMinutes] = a.split(':').map(Number);
                                                const [bHours, bMinutes] = b.split(':').map(Number);
                                                return (aHours * 60 + aMinutes) - (bHours * 60 + bMinutes);
                                              }).join(', ')}</div>
                                            )}
                                            {(rec.costMin || rec.costMax) && (
                                              <div><strong>Cost:</strong>
                                                {rec.costMin && rec.costMax ? ` $${rec.costMin} - $${rec.costMax}` :
                                                 rec.costMin ? ` From $${rec.costMin}` :
                                                 rec.costMax ? ` Up to $${rec.costMax}` : ''}
                                              </div>
                                            )}
                                            {rec.activityNotes && (
                                              <div><strong>Notes:</strong> {rec.activityNotes}</div>
                                            )}
                                            {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                          </div>
                                        </div>

                                        {/* Right Column - Client Selected Date */}
                                        <div className="recommendation-right-column">
                                          {rec.selectedDate && (
                                            <div className="recommendation-event-date">
                                              <strong style={{ color: '#0066cc', display: 'block', marginBottom: '4px' }}>Client Selected:</strong>
                                              {new Date(rec.selectedDate).toLocaleString('en-US', { 
                                                month: 'long', 
                                                day: 'numeric', 
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                  </div>
                                );
                                })
                            ) : (
                              <div style={{
                                padding: '40px',
                                textAlign: 'center',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                color: '#666'
                              }}>
                                <h4>Activities</h4>
                                <p>Fun activities and experiences will be available here.</p>
                              </div>
                            )}
                          </div>
                        )}

                        {/* Events */}
                        {activeRecosTab === 'events' && (
                          <div style={{ display: 'grid', gap: '15px', marginTop: '15px' }}>
                            {proposal.recommendations && proposal.recommendations.filter(rec => rec.recType === 'events').length > 0 ? (
                              proposal.recommendations
                                .filter(rec => rec.recType === 'events')
                                .map((rec, index) => {
                                  // Find the actual index in the full recommendations array
                                  const actualIndex = proposal.recommendations.findIndex(r => r === rec);
                                  return (
                                    <div key={index} className={`recommendation-box ${rec.isInterested ? 'interested' : ''}`}>
                                      {/* Delete button - only for admin */}
                                      {user?.role === 'admin' && (
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteRecommendation(actualIndex);
                                          }}
                                          className="recommendation-delete-btn"
                                          title="Delete recommendation"
                                        >
                                          ×
                                        </button>
                                      )}
                                      
                                      {/* Two Column Layout */}
                                      <div className="recommendation-header-grid">
                                        {/* Left Column - Title and Details */}
                                        <div className="recommendation-left-column">
                                          <h4 className="recommendation-title">{rec.eventTitle}</h4>

                                          {/* Details */}
                                          <div className="recommendation-details">
                                            <div><strong>Type:</strong> {rec.eventType?.replace(/\b\w/g, l => l.toUpperCase())}</div>
                                            {rec.venue && <div><strong>Venue:</strong> {rec.venue}</div>}
                                            {(rec.eventCostMin || rec.eventCostMax) && (
                                              <div><strong>Cost:</strong>
                                                {rec.eventCostMin && rec.eventCostMax ? ` $${rec.eventCostMin} - $${rec.eventCostMax}` :
                                                 rec.eventCostMin ? ` From $${rec.eventCostMin}` :
                                                 rec.eventCostMax ? ` Up to $${rec.eventCostMax}` : ''}
                                              </div>
                                            )}
                                            {rec.eventNotes && (
                                              <div><strong>Notes:</strong> {rec.eventNotes}</div>
                                            )}
                                            {rec.destination && <div><strong>Destination:</strong> {rec.destination}</div>}
                                          </div>
                                        </div>

                                        {/* Right Column - Event Date/Time */}
                                        <div className="recommendation-right-column">
                                          {rec.eventDateTime && (
                                            <div className="recommendation-event-date">
                                              <strong style={{ color: '#333', display: 'block', marginBottom: '4px' }}>Date & Time:</strong>
                                              {new Date(rec.eventDateTime).toLocaleString('en-US', {
                                                month: 'long',
                                                day: 'numeric',
                                                year: 'numeric',
                                                hour: 'numeric',
                                                minute: '2-digit',
                                                hour12: true
                                              })}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                  </div>
                                );
                                })
                            ) : (
                              <div style={{
                                padding: '40px',
                                textAlign: 'center',
                                border: '2px dashed #ddd',
                                borderRadius: '8px',
                                color: '#666'
                              }}>
                                <h4>Events</h4>
                                <p>Local events and happenings will be available here.</p>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

              </div>
          </div>
        </div>

        {/* Add Hotel Modal - Updated to handle editing */}
        {showAddHotel && (
          <AddHotelModal
            proposalId={id}
            proposal={proposal}
            editingHotel={editingHotel} // Pass the hotel being edited
            onSave={(updatedProposal) => {
              // Handle server response format: { data: proposal }
              if (updatedProposal && updatedProposal.hotels) {
                // Received full proposal object from server
                setProposal(updatedProposal);
              } else if (Array.isArray(updatedProposal)) {
                // Received just hotels array (legacy support)
                setProposal(prevProposal => ({
                  ...prevProposal,
                  hotels: updatedProposal
                }));
              } else {
                // Received single hotel object - add it to existing hotels
                setProposal(prevProposal => ({
                  ...prevProposal,
                  hotels: [...(prevProposal.hotels || []), updatedProposal]
                }));
              }
              setShowAddHotel(false);
              setEditingHotel(null);
            }}
            onClose={() => {
              setShowAddHotel(false);
              setEditingHotel(null);
            }}
          />
        )}

        {/* Add Rec Modal */}
        {showAddRec && (
          <AddProposalRec
            proposalId={id}
            proposal={proposal}
            onRecAdded={() => {
              // Refresh proposal data to show the new recommendation
              fetchProposal();
              setShowAddRec(false);
            }}
            onClose={() => {
              setShowAddRec(false);
            }}
          />
        )}

        {/* Edit Proposal Modal */}
        {showEditProposal && (
          <CreateProposalModal
            isEditing={true}
            existingProposal={proposal}
            onSave={(updatedProposal) => {
              setProposal(updatedProposal);
              setShowEditProposal(false);
            }}
            onClose={() => setShowEditProposal(false)}
          />
        )}

        {/* Preview as Client */}
        {previewAsClient && (
          <div style={{ 
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'white', 
            zIndex: 9999,
            overflow: 'auto'
          }}>
            <div style={{ 
              padding: '20px',
              borderBottom: '1px solid #ddd',
              backgroundColor: '#f8f9fa',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, color: '#666' }}>Client Preview - {proposal.title}</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button 
                  onClick={handleRefresh}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                  title="Refresh proposal data"
                >
                  Refresh
                </button>
                <button 
                  onClick={handleExitPreview}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Exit Preview
                </button>
              </div>
            </div>
            <ProposalViewer proposal={proposal} />
          </div>
        )}

        {/* Convert to Itinerary Modal */}
        {showConvertModal && proposal && (
          <ConvertToItineraryModal
            proposal={proposal}
            onClose={handleConvertModalClose}
            onConvert={handleConvertSubmit}
          />
        )}
    </div>
  );
}

export default ProposalDetails;